import i18n from "@/i18n";
import { parseServerErrorBody, isValidationBody, type ParsedServerBody } from "./server-error-body";

export type FriendlyErrorKind =
  | "network"
  | "timeout"
  | "server"
  | "rateLimited"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "validation"
  | "generic";

export type FriendlyAction = "retry" | "goHome" | "signIn" | "contactSupport" | "refresh";

export interface FriendlyError {
  readonly kind: FriendlyErrorKind;
  readonly title: string;
  readonly description: string;
  readonly actions: ReadonlyArray<FriendlyAction>;
  readonly status: number | null;
  readonly raw: string;
}

interface ParsedError {
  readonly status: number | null;
  readonly raw: string;
  readonly serverMessage: string | null;
  /** VP-231: sparsowana koperta `{ code, message, issues? }` z backendu (null gdy body nie-JSON) */
  readonly serverBody: ParsedServerBody | null;
  readonly isTimeout: boolean;
  readonly isNetwork: boolean;
}

const STATUS_PREFIX = /^(\d{3}):\s*(.*)$/s;

const KIND_ACTIONS: Record<FriendlyErrorKind, ReadonlyArray<FriendlyAction>> = {
  network: ["retry", "refresh"],
  timeout: ["retry", "goHome"],
  server: ["retry", "contactSupport"],
  rateLimited: ["retry"],
  unauthorized: ["signIn"],
  forbidden: ["goHome", "contactSupport"],
  notFound: ["goHome"],
  validation: ["retry"],
  generic: ["retry", "contactSupport"],
};

export class FriendlyErrorMapper {
  public static map(error: unknown): FriendlyError {
    const parsed = this.parse(error);
    const kind = this.classify(parsed);
    // VP-231: 400 bez błędu walidacji (np. "Brak tokenów") niesie własny, zlokalizowany
    // po stronie backendu message — pokazujemy go zamiast generycznego opisu.
    const useServerMessage =
      kind === "generic" &&
      (parsed.status === 400 || parsed.status === 422) &&
      !!parsed.serverBody?.message;
    return {
      kind,
      title: i18n.t(`commonErrors.title.${kind}`),
      description: useServerMessage
        ? parsed.serverBody!.message!
        : i18n.t(`commonErrors.description.${kind}`),
      actions: KIND_ACTIONS[kind],
      status: parsed.status,
      raw: parsed.raw,
    };
  }

  public static actionLabel(action: FriendlyAction): string {
    return i18n.t(`commonErrors.action.${action}`);
  }

  private static parse(error: unknown): ParsedError {
    if (error instanceof Error) {
      const raw = error.message ?? "";
      const isTimeout = raw.startsWith("REQUEST_TIMEOUT") || /timeout/i.test(raw);
      const isNetwork =
        !isTimeout &&
        (/^Failed to fetch/i.test(raw) || /NetworkError/i.test(raw) || /Network error/i.test(raw));

      const match = raw.match(STATUS_PREFIX);
      if (match) {
        const status = Number(match[1]);
        const serverMessage = match[2] || null;
        return {
          status: Number.isFinite(status) ? status : null,
          raw,
          serverMessage,
          serverBody: parseServerErrorBody(serverMessage),
          isTimeout,
          isNetwork,
        };
      }
      return { status: null, raw, serverMessage: null, serverBody: null, isTimeout, isNetwork };
    }

    const raw = typeof error === "string" ? error : String(error ?? "");
    return {
      status: null,
      raw,
      serverMessage: null,
      serverBody: null,
      isTimeout: false,
      isNetwork: false,
    };
  }

  private static classify(parsed: ParsedError): FriendlyErrorKind {
    if (parsed.isTimeout) return "timeout";
    if (parsed.isNetwork) return "network";
    const status = parsed.status;
    if (status === null) return "generic";
    if (status === 401) return "unauthorized";
    if (status === 403) return "forbidden";
    if (status === 404) return "notFound";
    // VP-231: 400 to nie zawsze błąd formularza — "Brak tokenów" i podobne mają własny kod
    // i zlokalizowany message w body. Walidacją jest tylko realny błąd walidacji (issues / kod).
    if (status === 422 || status === 400) {
      return isValidationBody(parsed.serverBody) ? "validation" : "generic";
    }
    if (status === 429) return "rateLimited";
    if (status >= 500) return "server";
    return "generic";
  }
}
